import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

/**
 * Consolidated Team Job Tracker — one printable sheet for the whole shop floor for a given day.
 * Landscape 11×17 to fit ~12–14 rows with all fields blank for hand-fill.
 */
const iso = (d) => d.toISOString().slice(0, 10);
const I18N = {
  en: { title: "DAILY JOB TRACKER", staffCount: "STAFF", printed: "Printed", noStaff: "No staff scheduled on", header: { num: "#", worker: "Worker", ops: "Operation(s)", shift: "Shift", in: "In", out: "Out", bs: "Break Start", be: "Break End", qty: "Qty per Operation", total: "Total", sig: "Signature" }, reviewed: "Reviewed by:", date: "Date:", footer: "Post at shop-floor station. Collect at day end.", all: "All workers fill this one sheet." },
  es: { title: "REGISTRO DIARIO", staffCount: "PERSONAL", printed: "Impreso", noStaff: "Sin personal programado el", header: { num: "#", worker: "Trabajador", ops: "Operación(es)", shift: "Turno", in: "Entrada", out: "Salida", bs: "Inicio Descanso", be: "Fin Descanso", qty: "Cant. por Operación", total: "Total", sig: "Firma" }, reviewed: "Revisado por:", date: "Fecha:", footer: "Colocar en la estación. Recoger al final del día.", all: "Todos los trabajadores completan una sola hoja." },
};

const prettyDate = (d) => d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function TeamJobTracker() {
  const [params, setParams] = useSearchParams();
  const dateParam = params.get("date") || iso(new Date());
  const [date, setDate] = useState(dateParam);
  const langMode = params.get("lang") || "en";
  const setLangMode = (v) => { const p = new URLSearchParams(params); p.set("lang", v); p.set("date", date); setParams(p, { replace: true }); };
  const L = I18N[langMode === "es" ? "es" : "en"];
  const both = langMode === "both";

  const [schedules, setSchedules] = useState([]);
  const [settings, setSettings] = useState({ company_name: "US Operations" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [sch, s] = await Promise.all([
          api.get(`/schedules?date=${date}`),
          api.get("/settings"),
        ]);
        const sorted = [...sch.data].sort((a, b) => (a.staff_name || "").localeCompare(b.staff_name || ""));
        setSchedules(sorted);
        setSettings(s.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load");
      } finally { setLoading(false); }
    })();
  }, [date]);

  const dateObj = useMemo(() => { const [y, m, d] = date.split("-").map(Number); return new Date(y, m - 1, d); }, [date]);
  const totalStaff = schedules.length;

  return (
    <div data-testid="team-tracker-page">
      <style>{`
        @page { size: 17in 11in; margin: 0.25in; }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .tracker-page { page-break-after: avoid; }
        }
        .tracker-page {
          width: 17in;
          min-height: 10.5in;
          padding: 0.15in 0.25in;
          background: #fff;
          color: #0f172a;
          font-family: 'IBM Plex Mono', monospace;
          box-sizing: border-box;
          margin: 12px auto;
          border: 2px solid #0f172a;
        }
        .tracker-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 11px;
        }
        .tracker-table th, .tracker-table td {
          border: 1.5px solid #0f172a;
          padding: 5px 6px;
          vertical-align: top;
        }
        .tracker-table th {
          background: #f1f5f9;
          font-family: 'Archivo Black', sans-serif;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-size: 10px;
          text-align: left;
        }
        .tracker-fill { height: 44px; }
      `}</style>

      <div className="flex items-center justify-between p-4 no-print flex-wrap gap-3">
        <Link to="/schedule" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back to Schedule
        </Link>
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <label className="font-mono-ui">Date:</label>
          <input
            type="date"
            value={date}
            onChange={(e) => { setDate(e.target.value); const p = new URLSearchParams(params); p.set("date", e.target.value); setParams(p); }}
            className="brut-input py-1"
            data-testid="team-tracker-date"
          />
          <div className="inline-flex border-2 border-slate-900" data-testid="tt-lang-toggle">
            {["en", "es", "both"].map((l) => (
              <button key={l} onClick={() => setLangMode(l)} className={`px-2 py-1 font-display uppercase ${langMode === l ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`tt-lang-${l}`}>
                {l === "both" ? "EN+ES" : l.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="font-mono-ui">
            {totalStaff} scheduled
          </div>
        </div>
        <button
          onClick={() => window.print()}
          className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2"
          data-testid="print-team-tracker-btn"
          disabled={loading || totalStaff === 0}
        >
          <Printer size={14} weight="bold" /> Print / Save PDF
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center font-display uppercase">Loading…</div>
      ) : error ? (
        <div className="p-8 text-center text-red-700 font-mono-ui">{error}</div>
      ) : totalStaff === 0 ? (
        <div className="p-8 text-center text-slate-500 font-display uppercase">No staff scheduled on {date}</div>
      ) : (
        <div className="tracker-page" data-testid="team-tracker-sheet">
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #0f172a", paddingBottom: 8 }}>
            <img src="/logo-gate.jpg" alt="GATE" style={{ width: 54, height: 54, border: "2px solid #0f172a", objectFit: "cover" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11, letterSpacing: 3, color: "#1d4ed8" }}>
                // {settings.company_name?.toUpperCase()} — SHOP FLOOR
              </div>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 34, lineHeight: 1, marginTop: 4 }}>
                {L.title}
              </div>
              {both && <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 18, lineHeight: 1, marginTop: 2, color: "#334155" }}>{I18N.es.title}</div>}
              <div style={{ fontSize: 13, marginTop: 3 }}>
                <b>{prettyDate(dateObj)}</b>
              </div>
            </div>
            <div style={{ textAlign: "right", fontSize: 10, color: "#64748b" }}>
              <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 14, color: "#0f172a" }}>
                {totalStaff} {L.staffCount}
              </div>
              <div style={{ marginTop: 3 }}>{L.printed} {new Date().toLocaleString()}</div>
              <div style={{ marginTop: 3, fontStyle: "italic" }}>{L.all}</div>
            </div>
          </div>

          {/* Table */}
          <table className="tracker-table" style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th style={{ width: "3%" }}>{L.header.num}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.num}</div>}</th>
                <th style={{ width: "12%" }}>{L.header.worker}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.worker}</div>}</th>
                <th style={{ width: "17%" }}>{L.header.ops}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.ops}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.shift}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.shift}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.in}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.in}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.out}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.out}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.bs}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.bs}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.be}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.be}</div>}</th>
                <th style={{ width: "20%" }}>{L.header.qty}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.qty}</div>}</th>
                <th style={{ width: "6%" }}>{L.header.total}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.total}</div>}</th>
                <th style={{ width: "7%" }}>{L.header.sig}{both && <div style={{ fontSize: 8, opacity: 0.7 }}>{I18N.es.header.sig}</div>}</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s, i) => {
                const ops = (s.operations && s.operations.length > 0)
                  ? s.operations
                  : (s.operation_code ? [{ code: s.operation_code, name: s.operation_name }] : []);
                return (
                  <tr key={s.id} className="tracker-fill">
                    <td style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 14, textAlign: "center" }}>{i + 1}</td>
                    <td>
                      <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 12, lineHeight: 1.1 }}>{s.staff_name}</div>
                      <div style={{ fontSize: 9, color: "#64748b", marginTop: 2 }}>{s.staff_designation}</div>
                    </td>
                    <td>
                      {ops.map((op, oi) => (
                        <div key={oi} style={{ marginTop: oi > 0 ? 3 : 0, borderLeft: `3px solid ${op.color || "#cbd5e1"}`, paddingLeft: 4 }}>
                          <span style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11, color: op.color || "#0f172a" }}>{op.code}</span>
                          <span style={{ fontSize: 9, marginLeft: 4, color: "#334155" }}>{op.name}</span>
                        </div>
                      ))}
                    </td>
                    <td style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 11 }}>{s.shift_start}<br />{s.shift_end}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td>
                      {ops.map((op, oi) => (
                        <div key={oi} style={{ marginTop: oi > 0 ? 3 : 0, display: "flex", justifyContent: "space-between", borderBottom: "1px dotted #64748b", paddingBottom: 2 }}>
                          <span style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 10 }}>{op.code}:</span>
                          <span style={{ minWidth: 40 }}> </span>
                        </div>
                      ))}
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Footer */}
          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderTop: "2px solid #0f172a", paddingTop: 8, fontSize: 10 }}>
            <div style={{ display: "flex", gap: 20 }}>
              <div><b>{L.reviewed}</b> ______________________</div>
              <div><b>{L.date}</b> ______________</div>
            </div>
            <div style={{ fontStyle: "italic", color: "#64748b" }}>{L.footer}</div>
          </div>
        </div>
      )}
    </div>
  );
}
